import { useTranslation } from 'react-i18next';
import { ScreenBackdrop } from './ScreenBackdrop';
import { SendReceiveButtons } from './SendReceiveButtons';
import { CountUp } from './CountUp';
import { TodayChange } from './TodayChange';
import { EyeIcon, EyeOffIcon } from './icons';
import { useWallet } from '../../hooks/useWallet';
import { useTotalBalance } from '../../hooks/useTotalBalance';
import { useMarkets } from '../../hooks/useCoinMarket';
import { formatTokenAmount, formatUsd } from '../../lib/format';
import { HIDDEN_BALANCE, INCPT_COIN_ID, useUiStore } from '../../store/ui';
import { COINS, UNIQUE_COINS, coinKey, type Coin } from './TokenSheet';
import { formatPrice } from '../../lib/presale';
import { useIncptAsset } from '../../hooks/useIncptAsset';
import { IncptIcon } from '../icons/IncptIcon';
import { formatDecimalDisplay } from '@incpt/shared';
import walletShieldImg from '../../assets/incpt_shield_growth_miniapp.webp';

type Props = {
  onSend: () => void;
  onReceive: () => void;
  onSwap: () => void;
  onSelectCoin: (coin: Coin) => void;
  onIncpt: () => void;
};

/** INCPT valued at reference price — whole dollars keep the row readable. */
const fmtIncptUsd = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;

function fmtSpot(p: number | null): string {
  if (p == null) return '—';
  return `$${p.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function WalletMainScreen({
  onSend,
  onReceive,
  onSwap,
  onSelectCoin,
  onIncpt,
}: Props) {
  const { t } = useTranslation();
  const { byTokenChain, byTokenChainUsd, priceBySymbol } = useWallet();
  const { walletUsd, incptUsd } = useTotalBalance();
  const incpt = useIncptAsset();
  const { bySymbol: markets } = useMarkets();
  const balanceHidden = useUiStore((s) => s.balanceHidden);
  const coinBalanceHidden = useUiStore((s) => s.coinBalanceHidden);
  const toggleBalanceHidden = useUiStore((s) => s.toggleBalanceHidden);

  const rowHidden = (coin: Coin) =>
    balanceHidden ||
    COINS.filter((c) => c.symbol === coin.symbol).some((c) => !!coinBalanceHidden[coinKey(c)]);
  const incptHidden = balanceHidden || !!coinBalanceHidden[INCPT_COIN_ID];

  // Same order as TokenSheet: stables → INCPT → natives.
  const stableCoins = UNIQUE_COINS.filter((c) => c.symbol === 'USDT' || c.symbol === 'USDC');
  const otherCoins = UNIQUE_COINS.filter((c) => c.symbol !== 'USDT' && c.symbol !== 'USDC');
  const showIncpt = incpt.visible;

  const coinRow = (coin: Coin, bordered: boolean) => {
    // Sum across all chains for this symbol — token amount and USD are
    // SEPARATE figures now (natives are not $1/token).
    const chains = COINS.filter((c) => c.symbol === coin.symbol);
    const amount = chains.reduce(
      (sum, c) => sum + (byTokenChain[`${c.symbol}:${c.chain}`] ?? 0),
      0,
    );
    const usd = chains.reduce(
      (sum, c) => sum + (byTokenChainUsd[`${c.symbol}:${c.chain}`] ?? 0),
      0,
    );
    const spot = markets[coin.symbol]?.price ?? priceBySymbol[coin.symbol] ?? null;
    const rawPct = markets[coin.symbol]?.change24h;
    const pct = rawPct == null ? null : Number(rawPct.toFixed(2));
    const changeUp = (pct ?? 0) >= 0;
    const hidden = rowHidden(coin);

    return (
      <button
        key={coin.symbol}
        onClick={() => onSelectCoin(coin)}
        className={`flex w-full items-center gap-3 p-4 text-left active:bg-black/[0.02] ${
          bordered ? 'border-t border-black/[0.06]' : ''
        }`}
      >
        <coin.Icon size={44} />
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-[16px] font-medium leading-tight text-ink">
              {coin.name}
            </span>
            <span className="shrink-0 rounded-full bg-black/[0.06] px-1.5 py-0.5 text-[11px] font-medium leading-none text-muted">
              {coin.symbol}
            </span>
          </div>
          <div className="flex items-center gap-1.5 text-[13px] leading-none tabular-nums">
            <span className="text-muted">{fmtSpot(spot)}</span>
            {pct != null && (
              <span className={changeUp ? 'text-positive' : 'text-negative'}>
                {pct > 0 ? '+' : pct < 0 ? '−' : ''}
                {Math.abs(pct).toFixed(2)}%
              </span>
            )}
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5 text-right">
          <span className="text-[16px] font-medium leading-none tabular-nums text-ink">
            {hidden ? HIDDEN_BALANCE : `$${formatUsd(usd)}`}
          </span>
          <span className="text-[13px] leading-none tabular-nums text-muted">
            {hidden ? '••••' : `${formatTokenAmount(amount, coin.symbol)} ${coin.symbol}`}
          </span>
        </div>
      </button>
    );
  };

  return (
    <div className="relative min-h-screen">
      <ScreenBackdrop full inTree={false} />
      <div className="nav-pad relative z-10 flex min-h-screen flex-col pt-[calc(var(--tg-safe-top)_+_0.75rem)]">
        <h1 className="text-center text-[16px] font-semibold tracking-[-0.2px] text-ink">
          {t('nav.wallet')}
        </h1>

        {/* Hero — shield illustration + total under protection */}
        <div className="flex flex-col items-center px-5 text-center">
          <div className="relative flex h-[160px] w-full items-center justify-center">
            <img
              src={walletShieldImg}
              alt=""
              className="pointer-events-none h-[160px] w-auto max-w-[280px] select-none object-contain"
            />
          </div>
          <div className="mt-4 flex items-center gap-2">
            {balanceHidden ? (
              <span className="text-[34px] font-bold leading-none tracking-[-1.4px] tabular-nums text-ink">
                {HIDDEN_BALANCE}
              </span>
            ) : (
              <CountUp
                value={walletUsd + incptUsd}
                className="text-[34px] font-bold leading-none tracking-[-1.4px] tabular-nums text-ink"
              />
            )}
            <button
              type="button"
              onClick={toggleBalanceHidden}
              aria-label={balanceHidden ? t('wallet.showBalance') : t('wallet.hideBalance')}
              className="flex h-9 w-9 items-center justify-center rounded-full text-muted active:bg-black/[0.05]"
            >
              {balanceHidden ? <EyeOffIcon size={22} /> : <EyeIcon size={22} />}
            </button>
          </div>
          <span className="mt-1.5 text-[13px] text-muted">{t('wallet.balance')}</span>
          <TodayChange className="mt-3" />
        </div>

        {/* Receive / Send */}
        <div className="mt-4 px-5">
          <SendReceiveButtons onReceive={onReceive} onSend={onSend} onSwap={onSwap} />
        </div>

        {/* Assets — INCPT under stables; buy is on its page. */}
        <div className="mt-6 px-5">
          <h2 className="text-[16px] font-medium text-ink">{t('wallet.myAssets')}</h2>
        </div>
        <div className="mx-5 mt-4 overflow-hidden rounded-[24px] bg-white shadow-card">
          {stableCoins.map((coin, i) => coinRow(coin, i > 0))}

          {showIncpt && (
            <button
              onClick={onIncpt}
              className={`flex w-full items-center gap-3 p-4 text-left active:bg-black/[0.02] ${
                stableCoins.length > 0 ? 'border-t border-black/[0.06]' : ''
              }`}
            >
              <IncptIcon size={44} scale={1.2} />
              <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                <div className="flex items-center gap-1.5">
                  <span className="truncate text-[16px] font-medium leading-tight text-ink">
                    {t('presale.coinName')}
                  </span>
                  <span className="shrink-0 rounded-full bg-black/[0.06] px-1.5 py-0.5 text-[11px] font-medium leading-none text-muted">
                    {incpt.symbol}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-[13px] leading-none tabular-nums">
                  <span className="text-muted">${formatPrice(incpt.price)}</span>
                  <span className={incpt.change24h >= 0 ? 'text-positive' : 'text-negative'}>
                    {incpt.change24h > 0 ? '+' : incpt.change24h < 0 ? '−' : ''}
                    {Math.abs(incpt.change24h).toFixed(2)}%
                  </span>
                </div>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1.5 text-right">
                <span className="text-[16px] font-medium leading-none tabular-nums text-ink">
                  {incptHidden ? HIDDEN_BALANCE : fmtIncptUsd(incpt.usd)}
                </span>
                <span className="text-[13px] leading-none tabular-nums text-muted">
                  {incptHidden
                    ? '••••'
                    : `${formatDecimalDisplay(incpt.amount)} ${incpt.symbol}`}
                </span>
              </div>
            </button>
          )}

          {otherCoins.map((coin, i) =>
            coinRow(coin, i > 0 || stableCoins.length > 0 || showIncpt),
          )}
        </div>
      </div>
    </div>
  );
}
